import React, { useState } from 'react';

const BRAND_BLUE = '#4841F0';

const TruncatedCell = ({ text, maxLen = 60 }) => {
  const [expanded, setExpanded] = useState(false);
  if (!text) return <span className="text-gray-300 text-xs">—</span>;
  if (text.length <= maxLen) return <span className="text-xs text-gray-700">{text}</span>;
  return (
    <span className="text-xs text-gray-700">
      {expanded ? text : `${text.slice(0, maxLen)}…`}{' '}
      <button
        onClick={() => setExpanded(!expanded)}
        className="text-indigo-500 hover:text-indigo-700 underline text-xs font-medium"
      >
        {expanded ? 'less' : 'more'}
      </button>
    </span>
  );
};

const getMonthKey = (ts) => {
  if (!ts) return null;
  const d = new Date(ts);
  return new Date(d.getFullYear(), d.getMonth()).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' });
};
const getQuarterKey = (ts) => {
  if (!ts) return null;
  const d = new Date(ts);
  return `Q${Math.ceil((d.getMonth() + 1) / 3)} ${d.getFullYear()}`;
};

const WeeklyReportsTable = ({ data, periodType = 'weekly' }) => {
  const [sortKey, setSortKey] = useState('weekNumber');
  const [sortDir, setSortDir] = useState('desc');

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const sorted = [...data].sort((a, b) => {
    const va = a[sortKey] ?? '';
    const vb = b[sortKey] ?? '';
    const dir = sortDir === 'asc' ? 1 : -1;
    if (typeof va === 'number') return (va - vb) * dir;
    return String(va).localeCompare(String(vb)) * dir;
  });

  const SortIcon = ({ col }) => {
    if (sortKey !== col) return <span className="ml-1 text-gray-300">↕</span>;
    return <span className="ml-1" style={{ color: BRAND_BLUE }}>{sortDir === 'asc' ? '↑' : '↓'}</span>;
  };

  const Th = ({ col, label, className = '' }) => (
    <th
      className={`px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer select-none whitespace-nowrap hover:bg-gray-100 transition-colors ${className}`}
      onClick={() => handleSort(col)}
    >
      {label}<SortIcon col={col} />
    </th>
  );

  return (
    <div className="dashboard-card overflow-hidden">
      {/* Table header */}
      <div className="px-5 py-4 border-b border-gray-100">
        <h3 className="text-base font-semibold text-gray-800">Submission Detail</h3>
        <p className="text-xs text-gray-500 mt-0.5">{sorted.length} record{sorted.length !== 1 ? 's' : ''} shown</p>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-100">
          <thead className="bg-gray-50">
            <tr>
              <Th col="weekNumber" label={periodType === 'weekly' ? 'Week' : periodType === 'monthly' ? 'Month' : 'Quarter'} />
              <Th col="tpName" label="TP" />
              <Th col="client" label="Client" />
              <Th col="openPositions" label="Open Pos." />
              <Th col="screeningCalls" label="Screenings" />
              <Th col="passedAfterScreening" label="Passed" />
              <Th col="finalStages" label="Finals" />
              <Th col="offersAccepted" label="Accepted" />
              <Th col="offersRejected" label="Rejected" />
              <th className="px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Blockers</th>
              <th className="px-3 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Achievements</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-50">
            {sorted.length === 0 ? (
              <tr>
                <td colSpan={11} className="px-5 py-10 text-center text-sm text-gray-400">
                  No submissions found
                </td>
              </tr>
            ) : (
              sorted.map(row => (
                <tr key={row.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-3 py-3 text-sm font-semibold text-gray-700 whitespace-nowrap">
                    <span
                      className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium text-white"
                      style={{ background: BRAND_BLUE }}
                    >
                      {periodType === 'weekly'
                        ? `Wk ${row.weekNumber}`
                        : periodType === 'monthly'
                        ? getMonthKey(row.timestamp) || '—'
                        : getQuarterKey(row.timestamp) || '—'}
                    </span>
                  </td>
                  <td className="px-3 py-3 text-sm font-medium text-gray-800 whitespace-nowrap">{row.tpName || '—'}</td>
                  <td className="px-3 py-3 text-sm text-gray-700 whitespace-nowrap">{row.client || '—'}</td>
                  <td className="px-3 py-3 text-sm text-center text-gray-700">{row.openPositions}</td>
                  <td className="px-3 py-3 text-sm text-center font-medium" style={{ color: BRAND_BLUE }}>{row.screeningCalls}</td>
                  <td className="px-3 py-3 text-sm text-center text-indigo-600">{row.passedAfterScreening}</td>
                  <td className="px-3 py-3 text-sm text-center text-purple-600">{row.finalStages}</td>
                  <td className="px-3 py-3 text-sm text-center text-green-600 font-medium">{row.offersAccepted}</td>
                  <td className="px-3 py-3 text-sm text-center text-red-500">{row.offersRejected}</td>
                  <td className="px-3 py-3 max-w-xs"><TruncatedCell text={row.blockers} /></td>
                  <td className="px-3 py-3 max-w-xs"><TruncatedCell text={row.achievements} /></td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default WeeklyReportsTable;
